import { useEffect, useRef, useState, type ReactNode } from "react";
import { appName } from "../config";

// Branded replacement for the browser's alert()/confirm()/prompt() boxes.
// Call the helpers from anywhere (no hooks needed):
//
//   showSuccess("Listed for sale.")
//   showError(err, "Listing failed")                 // cleans up raw chain errors
//   if (!(await confirmAction({ ... }))) return;     // Promise<boolean>
//   const reason = await promptText({ ... });        // Promise<string | null>
//
// <DialogHost /> (mounted once in App.tsx) renders whatever is queued.

export type DialogVariant = "success" | "error" | "warning" | "info" | "danger";

interface DialogRequest {
  id: number;
  kind: "notify" | "confirm" | "prompt";
  variant: DialogVariant;
  title: string;
  message: ReactNode;
  details?: string;
  confirmLabel: string;
  cancelLabel?: string;
  placeholder?: string;
  resolve: (value: boolean | string | null) => void;
}

let nextId = 1;
let queue: DialogRequest[] = [];
const listeners = new Set<(q: DialogRequest[]) => void>();
const emit = () => listeners.forEach((l) => l([...queue]));

function enqueue(req: Omit<DialogRequest, "id" | "resolve">): Promise<boolean | string | null> {
  return new Promise((resolve) => {
    queue.push({ ...req, id: nextId++, resolve });
    emit();
  });
}

// ------------------------------------------------------------- friendly errors

/** Turns raw wallet / chain errors into something a person can act on. */
export function friendlyError(err: unknown, fallback: string): { message: string; details?: string } {
  const raw = err instanceof Error ? err.message : typeof err === "string" ? err : "";
  if (!raw) return { message: fallback };
  const cleaned = raw.replace(/^Error:\s*/i, "").replace(/^assertion failure with message:\s*/i, "").trim();
  const lower = cleaned.toLowerCase();

  const known: Array<[RegExp, string]> = [
    [/user (rejected|cancel|denied)|cancel(l)?ed|user closed|modal closed/, "You cancelled the request in your wallet. Nothing was sent."],
    [/billed cpu time|cpu usage|tx_cpu_usage_exceeded|not enough cpu/, "Your account ran out of CPU for this transaction. Wait a little, or stake/rent more CPU, then try again."],
    [/net usage|tx_net_usage_exceeded/, "Your account ran out of NET bandwidth. Stake more NET or try again later."],
    [/ram_usage_exceeded|insufficient ram/, "Your account doesn't have enough RAM for this. Buy a little RAM and try again."],
    [/overdrawn balance|insufficient funds|no balance object found/, "Your wallet doesn't have enough balance for this transaction."],
    [/expired transaction|transaction expired/, "The transaction expired before it was confirmed. Please try again."],
    [/duplicate transaction/, "This transaction was already submitted."],
    [/deposit amount must exactly match/, "The deposit amount must exactly match this NFT's registered backing amount."],
    [/no escrow rate registered/, "This template isn't registered for WAX backing yet."],
    [/no longer accepting new escrow deposits/, "Backing for this template is currently paused."],
    [/exchange is paused/, "Trading on the exchange is paused right now. You can still cancel open orders."],
    [/market is not enabled/, "This market is disabled right now. You can still cancel open orders."],
    [/below this market's minimum value/, "That order is below this market's minimum order value."],
    [/too small to buy anything/, "That amount is too small to buy even one unit at this price."],
    [/order not found/, "That order no longer exists — it may have just been filled or cancelled."],
    [/not your order/, "That order belongs to a different account."],
    [/admin authorization required/, "Only the exchange admin account can do this. Connect with the admin wallet."],
    [/not.*authorized|missing authority|missing required authority/, "Your account isn't authorized to do this."],
  ];
  for (const [re, msg] of known) if (re.test(lower)) return { message: msg, details: cleaned !== msg ? cleaned : undefined };
  return { message: `${fallback}.`, details: cleaned };
}

// ---------------------------------------------------------------- public API

export function showSuccess(message: ReactNode, title = "Success") {
  return enqueue({ kind: "notify", variant: "success", title, message, confirmLabel: "OK" }).then(() => undefined);
}

export function showInfo(message: ReactNode, title = "Heads up") {
  return enqueue({ kind: "notify", variant: "info", title, message, confirmLabel: "OK" }).then(() => undefined);
}

export function showWarning(message: ReactNode, title = "Warning") {
  return enqueue({ kind: "notify", variant: "warning", title, message, confirmLabel: "OK" }).then(() => undefined);
}

export function showError(err: unknown, fallback = "Something went wrong", title = "Transaction failed") {
  const { message, details } = friendlyError(err, fallback);
  return enqueue({ kind: "notify", variant: "error", title, message, details, confirmLabel: "OK" }).then(() => undefined);
}

export function confirmAction(opts: {
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}): Promise<boolean> {
  return enqueue({
    kind: "confirm",
    variant: opts.danger ? "danger" : "warning",
    title: opts.title,
    message: opts.message,
    confirmLabel: opts.confirmLabel ?? "Continue",
    cancelLabel: opts.cancelLabel ?? "Cancel",
  }).then((v) => v === true);
}

export function promptText(opts: { title: string; message: ReactNode; placeholder?: string; confirmLabel?: string }): Promise<string | null> {
  return enqueue({
    kind: "prompt",
    variant: "info",
    title: opts.title,
    message: opts.message,
    placeholder: opts.placeholder,
    confirmLabel: opts.confirmLabel ?? "Submit",
    cancelLabel: "Cancel",
  }).then((v) => (typeof v === "string" ? v : null));
}

// -------------------------------------------------------------------- icons

function VariantIcon({ variant }: { variant: DialogVariant }) {
  const common = { width: 28, height: 28, viewBox: "0 0 24 24", fill: "none", strokeWidth: 2.2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
  switch (variant) {
    case "success":
      return <svg {...common} stroke="currentColor"><circle cx="12" cy="12" r="10" /><path d="M7.5 12.5l3 3 6-6.5" /></svg>;
    case "error":
      return <svg {...common} stroke="currentColor"><circle cx="12" cy="12" r="10" /><path d="M15 9l-6 6M9 9l6 6" /></svg>;
    case "warning":
    case "danger":
      return <svg {...common} stroke="currentColor"><path d="M10.3 3.9L2.4 17.5A2 2 0 004.1 20.5h15.8a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" /><path d="M12 9.5v4.5M12 17.5h.01" /></svg>;
    default:
      return <svg {...common} stroke="currentColor"><circle cx="12" cy="12" r="10" /><path d="M12 11v5.5M12 7.5h.01" /></svg>;
  }
}

// --------------------------------------------------------------------- host

export function DialogHost() {
  const [items, setItems] = useState<DialogRequest[]>([]);
  const [text, setText] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const primaryRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    listeners.add(setItems);
    return () => void listeners.delete(setItems);
  }, []);

  const current = items[0];

  useEffect(() => {
    if (!current) return;
    setText("");
    setShowDetails(false);
    const t = setTimeout(() => (current.kind === "prompt" ? inputRef.current : primaryRef.current)?.focus(), 30);
    return () => clearTimeout(t);
  }, [current?.id]);

  if (!current) return null;

  const close = (value: boolean | string | null) => {
    current.resolve(value);
    queue = queue.filter((q) => q.id !== current.id);
    emit();
  };
  const cancelValue = current.kind === "prompt" ? null : current.kind === "confirm" ? false : true;

  return (
    <div
      className="wc-dialog-backdrop"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(cancelValue); }}
      onKeyDown={(e) => { if (e.key === "Escape") close(cancelValue); }}
    >
      <div className={`wc-dialog wc-dialog--${current.variant}`} role={current.kind === "notify" ? "alertdialog" : "dialog"} aria-modal="true" aria-labelledby="wc-dialog-title">
        <div className="wc-dialog__brand">
          <span className="wc-dialog__logo" aria-hidden="true">🪄</span>
          <span>{appName}</span>
        </div>
        <div className="wc-dialog__body">
          <div className="wc-dialog__icon"><VariantIcon variant={current.variant} /></div>
          <div className="wc-dialog__content">
            <h2 id="wc-dialog-title" className="wc-dialog__title">{current.title}</h2>
            <div className="wc-dialog__message">{current.message}</div>
            {current.kind === "prompt" && (
              <textarea
                ref={inputRef}
                className="wc-dialog__input"
                rows={3}
                placeholder={current.placeholder}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            )}
            {current.details && (
              <div className="wc-dialog__details">
                <button type="button" className="wc-dialog__details-toggle" onClick={() => setShowDetails((v) => !v)}>
                  {showDetails ? "Hide technical details" : "Show technical details"}
                </button>
                {showDetails && <code className="wc-dialog__details-text">{current.details}</code>}
              </div>
            )}
          </div>
        </div>
        <div className="wc-dialog__actions">
          {current.kind !== "notify" && (
            <button type="button" className="btn btn--outline" onClick={() => close(cancelValue)}>
              {current.cancelLabel}
            </button>
          )}
          <button
            ref={primaryRef}
            type="button"
            className={`btn ${current.variant === "danger" ? "btn--danger" : "btn--primary"}`}
            disabled={current.kind === "prompt" && !text.trim()}
            onClick={() => close(current.kind === "prompt" ? text.trim() : true)}
          >
            {current.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Shorthand for destructive confirmations (delete, ban, purge…). */
export function confirmDanger(message: ReactNode, confirmLabel = "Yes, continue", title = "Are you sure?"): Promise<boolean> {
  return confirmAction({ title, message, confirmLabel, danger: true });
}
